import { createSelector } from 'reselect';

import {
  getCompetitorIdFromProps,
  getFilterIdFromProps,
  getMatchIdFromProps,
  getPlayerIdFromProps,
  getSliderValueFromProps,
} from '../../../../selectors/_propsGetters_';
import { selectEntitiesOutcomes } from '../../../../selectors/entities/outcomes/entitiesOutcomesPrimary.selector';
import { type BetState } from '../../../../states/entities/bets';
import { type OutcomeState } from '../../../../states/entities/outcomes';
import { type BetTemplate } from '../../../../types/bet';
import { MarketIds } from '../../../../types/market';
import { getIsFromMultichoiceFromFilterId, getMarketIdFromFilterId } from '../../../../types/matchBetFilters';
import { type SelectorMaker, type Selector } from '../../../../types/selectors';
import { makeMatchBetsBySliderValueSelector, makeSBVFromFilterIdSelector } from '../../selectors/lineUp.selector';

const emptyArray: [] = [];

type LineUpPlayerOddsPropsType = {
  matchId?: number;
  betId?: number;
  oddId?: number;
  betTemplate?: BetTemplate;
  showPercentDistribution: boolean;
  isFromMultichoice: boolean;
};

const makeLineUpPlayerOddsPropsSelector: SelectorMaker<LineUpPlayerOddsPropsType> = () => {
  const selectMatchBetByMarketId: Selector<BetState | undefined> = createSelector(
    [
      makeMatchBetsBySliderValueSelector(),
      makeSBVFromFilterIdSelector(),
      getSliderValueFromProps,
      selectEntitiesOutcomes,
      getPlayerIdFromProps,
      getCompetitorIdFromProps,
      getFilterIdFromProps,
    ],
    (bets, specialBetValue, sliderValue, outcomes, playerId, competitorId, filterId) => {
      const marketId = getMarketIdFromFilterId(filterId);

      if (marketId === MarketIds.MARKET_ID_SOCCER_PLAYER_PROP_ASSISTS) {
        return bets.find((bet) => outcomes[bet.outcomes[0]]?.playerId === playerId);
      }

      if (competitorId) {
        return bets.find(
          (bet) => (specialBetValue && bet.specialBetValue === `${specialBetValue}`) || !specialBetValue,
        );
      }
      return bets.find((bet) =>
        sliderValue
          ? outcomes[bet.outcomes[0]]?.playerId === playerId
          : (specialBetValue && bet.specialBetValue === `${specialBetValue}`) || !specialBetValue,
      );
    },
  );

  const selectBetBetId: Selector<number | undefined> = createSelector([selectMatchBetByMarketId], (bet) => bet?.betId);

  const selectBetOutcomeIds: Selector<Array<number>> = createSelector(
    [selectMatchBetByMarketId],
    (bet) => bet?.outcomes ?? emptyArray,
  );

  const selectBetOutcomes: Selector<Array<OutcomeState>> = createSelector(
    [selectBetOutcomeIds, selectEntitiesOutcomes],
    (outcomeIds, outcomes) => outcomeIds?.map((outcomeId) => outcomes[outcomeId])?.filter((k) => k) ?? emptyArray,
  );

  const selectMatchOutcomeByPlayerId: Selector<OutcomeState | undefined> = createSelector(
    [selectBetOutcomes, getPlayerIdFromProps, getCompetitorIdFromProps, getFilterIdFromProps],
    (outcomes, playerId, competitorId, filterId) => {
      const marketId = getMarketIdFromFilterId(filterId);

      if (marketId === MarketIds.MARKET_ID_SOCCER_PLAYER_PROP_ASSISTS) {
        return outcomes.find((outcome) => {
          const { code } = outcome;
          const splitted = code.split(':');

          return splitted.length !== 1 && splitted[splitted.length - 1] === '1' && playerId === outcome.playerId;
        });
      }

      if (competitorId) {
        return outcomes.find((outcome) => outcome?.competitorId === competitorId);
      }

      return outcomes.find((outcome) => outcome.playerId === playerId);
    },
  );

  const selectMatchOutcomeOddId: Selector<number | undefined> = createSelector(
    [selectMatchOutcomeByPlayerId],
    (outcome) => outcome?.oddId,
  );

  const selectBetTemplate: Selector<BetTemplate | undefined> = createSelector(
    [selectMatchBetByMarketId],
    (bet) => bet?.template,
  );

  const selectIsFromMultichoiceFromFilterId: Selector<boolean> = createSelector(
    [getFilterIdFromProps],
    (filterId) => !!(filterId ? getIsFromMultichoiceFromFilterId(filterId) : undefined),
  );

  return createSelector(
    [
      getMatchIdFromProps,
      selectBetBetId,
      selectMatchOutcomeOddId,
      selectBetTemplate,
      selectIsFromMultichoiceFromFilterId,
    ],
    (matchId, betId, oddId, betTemplate, isFromMultichoice) => {
      return {
        matchId,
        betId,
        oddId,
        betTemplate,
        showPercentDistribution: false,
        isFromMultichoice,
      };
    },
  );
};

export default makeLineUpPlayerOddsPropsSelector;
